// src/features/product/pages/ProductsPage.jsx
import React, { useEffect, useState } from "react";
import { Button, Container } from "react-bootstrap";
import NavbarComp from "../../shared/components/Navbar";
import { useProducts } from "./hooks/useProducts";
import ProductTable from "./components/ProductTable";
import { IoAddCircleOutline } from "react-icons/io5";
import ProductModalAdd from "./components/ProductModalAdd";
import ProductModalAddDetail from "./components/ProductModalAddDetail";
import { useProductDetails } from "./hooks/useProductDetails";
import ProductModalAddDetailCategories from "./components/ProductModalAddDetailCategories";
import { useProductCategoriesDetails } from "./hooks/useProductCategoriesDetails";
import Swal from "sweetalert2";
import { useAuth } from "../auth/hooks/useAuth";
import AlertMessage from "../../shared/components/Alert";
import { useProductExternal } from "./hooks/useProductExternal";

const ProductsPage = () => {
  const { validate } = useAuth();
  const {
    products,
    addProduct,
    editProduct,
    removeProduct,
    editDescriptionProduct,
    fetchProducts,
    updateOnActive,
    alert,
  } = useProducts();

  const { addProductDetails, editProductDetails } = useProductDetails();
  const { addProductCategoriesDetails } = useProductCategoriesDetails();
  const {
    loading,
    error,
    productExternal,
    fetchProductExternalById,
    fetchProductExternal,
  } = useProductExternal();

  // State untuk modal utama (tambah/edit produk)
  const [showModal, setShowModal] = useState(false);
  const [editingData, setEditingData] = useState(null);

  // State untuk modal detail (deskripsi, dll)
  const [showModalDetail, setShowModalDetail] = useState(false);
  const [editingDataDetail, setEditingDataDetail] = useState(null);

  // State untuk modal kategori detail
  const [showModalCategoriesDetail, setShowModalCategoriesDetail] =
    useState(false);
  const [dataCategoriesDetail, setDataCategoriesDetail] = useState(null);

  // ===================== HANDLER MODAL UTAMA =====================
  // Buka modal untuk TAMBAH produk (reset editingData)
  const handleOpenAddModal = async () => {
    setEditingData(null); // pastikan kosong

     Swal.fire({
      title: 'Sedang sync product...',
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });
      try {
      await fetchProductExternal(); // panggil endpoint sync
      Swal.close(); // tutup loading

      // Notifikasi sukses (toast kecil)
      Swal.fire({
        icon: 'success',
        title: 'Sync berhasil!',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 2000,
      });

      setShowModal(true); // buka modal tambah produk
    } catch (err) {
      Swal.close(); // tutup loading
      Swal.fire({
        icon: 'error',
        title: 'Sync gagal',
        text: error || 'Gagal menyinkronkan produk eksternal.',
      });
    }
    // setShowModal(true);
  };

  // Buka modal untuk EDIT produk (isi editingData)
  const handleEditProduct = (product) => {
    setEditingData(product);
    setShowModal(true);
  };

  // Tutup modal utama
  const handleCloseModal = () => {
    setShowModal(false);
    setEditingData(null); // reset setelah ditutup
  };

  // ===================== HANDLER MODAL DETAIL =====================
  const handleOpenModalDetail = () => setShowModalDetail(true);
  const handleCloseModalDetail = () => {
    setShowModalDetail(false);
    setEditingDataDetail(null);
  };

  const showModalAddDetail = (data) => {
    setEditingDataDetail(data);
    handleOpenModalDetail();
  };

  const handleEditProductDetails = (data) => {
    editProductDetails(data?.id, data);
  };

  // ===================== HANDLER MODAL KATEGORI DETAIL =====================
  const handleOpenModalCategoriesDetail = () =>
    setShowModalCategoriesDetail(true);
  const handleCloseModalCategoriesDetail = () => {
    setShowModalCategoriesDetail(false);
    setDataCategoriesDetail(null);
  };

  const handleProductCategoriesDetail = (data) => {
    setDataCategoriesDetail(data);
    handleOpenModalCategoriesDetail();
  };

  const addCategoriesDetail = (data) => {
    addProductCategoriesDetails(data);
  };

  // ===================== HANDLER LAINNYA =====================
  const handleDeleteProduct = (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "Do you really want to delete this item?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    }).then((result) => {
      if (result.isConfirmed) {
        removeProduct(id.id);
        Swal.fire("Deleted!", "The item has been deleted.", "success");
      }
    });
  };

  const handleEditDescriptionProduct = (product) => {
    editDescriptionProduct(product.id, product);
  };

  // ===================== FETCH DATA =====================
  useEffect(() => {
    fetchProducts();
    validate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <NavbarComp />
      <AlertMessage
        show={alert.show}
        variant={alert.variant}
        message={alert.message}
        onClose={() => {}}
      />
      <Container fluid className="mt-2 mt-md-5">
        {/* Tombol ADD produk */}
        <Button
          variant="outline-success"
          className="mb-2"
          onClick={handleOpenAddModal}
        >
          <IoAddCircleOutline size={32} />
        </Button>

        {/* Tabel produk */}
        <ProductTable
          products={products}
          onEdit={handleEditProduct}
          onDelete={handleDeleteProduct}
          modalAddDetail={showModalAddDetail}
          updateOnActive={updateOnActive}
        />
      </Container>

      {/* Modal Tambah / Edit Produk */}
      <ProductModalAdd
        products={products}
        show={showModal}
        handleClose={handleCloseModal}
        handleAddProduct={addProduct}
        editingData={editingData}
        parsingData={editingDataDetail}
        editProduct={editProduct}
      />

      {/* Modal Detail Produk (deskripsi, dll) */}
      <ProductModalAddDetail
        show={showModalDetail}
        handleClose={handleCloseModalDetail}
        parsingDataEdit={editingDataDetail}
        editDescriptionProduct={handleEditDescriptionProduct}
        addProductDetails={addProductDetails}
        editProductDetails={handleEditProductDetails}
        showModalAddCategoriesDetail={handleProductCategoriesDetail}
      />

      {/* Modal Kategori Detail */}
      <ProductModalAddDetailCategories
        show={showModalCategoriesDetail}
        handleClose={handleCloseModalCategoriesDetail}
        data={dataCategoriesDetail}
        addCategoriesDetail={addCategoriesDetail}
      />
    </>
  );
};

export default ProductsPage;
